import { useState, useEffect, useCallback } from "react";
import { http, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Edit, Eye, EyeOff, ChevronDown, X, Users, CreditCard, Download, Check, Copy, Clock } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";

const STATUSES = ["draft","published","registration_open","almost_full","sold_out","waitlist_open","registration_closed","cancelled","completed","archived"];
const STATUS_STYLE = { draft: "text-zinc-400 bg-zinc-800", published: "text-blue-400 bg-blue-900/20", registration_open: "text-green-400 bg-green-900/20", almost_full: "text-amber-400 bg-amber-900/20", sold_out: "text-red-400 bg-red-900/20", waitlist_open: "text-purple-400 bg-purple-900/20", registration_closed: "text-zinc-400 bg-zinc-800", cancelled: "text-red-400 bg-red-900/20", completed: "text-emerald-400 bg-emerald-900/20", archived: "text-zinc-500 bg-zinc-800" };

export default function AdminWorkshops() {
  const [tab, setTab] = useState("workshops");
  const [workshops, setWorkshops] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewingReg, setViewingReg] = useState(null);
  const [filterWS, setFilterWS] = useState("");

  const load = useCallback(async () => {
    try {
      const [w, r, wl] = await Promise.all([
        http.get("/workshops/admin/workshops"),
        http.get(`/workshops/admin/registrations${filterWS ? `?workshop_id=${filterWS}` : ""}`),
        http.get(`/workshops/admin/waitlist${filterWS ? `?workshop_id=${filterWS}` : ""}`),
      ]);
      setWorkshops(w.data); setRegistrations(r.data); setWaitlist(wl.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [filterWS]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  if (editing) return <WorkshopEditor workshop={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />;

  const tabs = [
    { key: "workshops", label: "Workshops", count: workshops.length },
    { key: "registrations", label: "Registrations", count: registrations.length },
    { key: "waitlist", label: "Waitlist", count: waitlist.length },
    { key: "testimonials", label: "Testimonials" },
    { key: "faq", label: "FAQ" },
    { key: "settings", label: "Settings" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-workshops">
      <h2 className="font-display text-3xl text-[#F4F1EA]">Workshop Manager</h2>
      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-full text-xs font-medium tracking-wide transition-all ${tab === t.key ? "bg-[#C9A055] text-[#1A1A1A]" : "border border-white/10 text-zinc-400"}`}>{t.label}{t.count != null ? <span className="ml-1 opacity-60">({t.count})</span> : null}</button>
        ))}
      </div>

      {/* Workshop filter */}
      {(tab === "registrations" || tab === "waitlist") && workshops.length > 0 && (
        <select value={filterWS} onChange={e => setFilterWS(e.target.value)} className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none">
          <option value="">All workshops</option>
          {workshops.map(w => <option key={w.id} value={w.id}>{w.title}</option>)}
        </select>
      )}

      {tab === "workshops" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <button onClick={() => setEditing({ id: null })} data-testid="ws-add"
              className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]">
              <Plus size={14} /> New Workshop
            </button>
          </div>
          {workshops.map(w => (
            <div key={w.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4" data-testid={`ws-admin-${w.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[w.status] || "bg-zinc-800 text-zinc-400"}`}>{w.status?.replace(/_/g, " ")}</span>
                  {w.featured && <span className="text-[10px] text-amber-400">Featured</span>}
                </div>
                <p className="text-[#F4F1EA] text-sm font-medium truncate">{w.title}</p>
                <p className="text-zinc-500 text-xs">{w.date} · {w.start_time} · {w.seats_remaining}/{w.capacity} seats</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEditing(w)} className="text-zinc-500 hover:text-[#C9A055] p-2"><Edit size={14} /></button>
                <button onClick={async () => { await http.post(`/workshops/admin/workshops/${w.id}/duplicate`); toast.success("Duplicated"); load(); }} className="text-zinc-500 hover:text-[#C9A055] p-2" title="Duplicate"><Copy size={14} /></button>
                <button onClick={async () => { if (!window.confirm("Delete?")) return; await http.delete(`/workshops/admin/workshops/${w.id}`); load(); }} className="text-zinc-500 hover:text-red-400 p-2"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "registrations" && (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap justify-end">
            {filterWS && (
              <>
                <button onClick={async () => {
                  const type = window.prompt("Reminder type:\n7day — One week away\n2day — Two days away\nmorning — Day of\ngeneral — Custom", "general");
                  if (!type) return;
                  try { const { data } = await http.post(`/workshops/admin/send-reminders/${filterWS}`, { type }); toast.success(`Sent ${data.sent} reminder emails`); }
                  catch { toast.error("Failed to send"); }
                }} className="flex items-center gap-2 border border-[#C9A055] text-[#C9A055] px-3 py-1.5 rounded-lg text-[10px] hover:bg-[#C9A055] hover:text-[#1A1A1A] transition-all" data-testid="ws-send-reminders">
                  <Clock size={12} /> Send Reminders
                </button>
                <button onClick={async () => {
                  if (!window.confirm("Send invitation to all waitlisted people for this workshop?")) return;
                  try { const { data } = await http.post(`/workshops/admin/send-waitlist-invite/${filterWS}`, {}); toast.success(`Sent ${data.sent} waitlist invitations`); }
                  catch { toast.error("Failed to send"); }
                }} className="flex items-center gap-2 border border-purple-400 text-purple-400 px-3 py-1.5 rounded-lg text-[10px] hover:bg-purple-400 hover:text-[#1A1A1A] transition-all" data-testid="ws-send-waitlist-invite">
                  <Users size={12} /> Invite Waitlist
                </button>
              </>
            )}
            <button onClick={async () => {
              try {
                const res = await http.get(`/workshops/admin/export/registrations${filterWS ? `?workshop_id=${filterWS}` : ""}`, { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement("a"); a.href = url; a.download = "workshop_registrations.csv"; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
              } catch { toast.error("Export failed"); }
            }}
              className="flex items-center gap-2 border border-[#C9A055] text-[#C9A055] px-3 py-1.5 rounded-lg text-[10px] hover:bg-[#C9A055] hover:text-[#1A1A1A] transition-all" data-testid="ws-export-csv">
              <Download size={12} /> Export CSV
            </button>
          </div>
          {registrations.length === 0 && <p className="text-zinc-500 text-center py-8">No registrations yet.</p>}
          {registrations.map(r => (
            <div key={r.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4" data-testid={`ws-reg-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-green-900/20 text-green-400" : r.payment_status === "pending_verification" ? "bg-amber-900/20 text-amber-400" : "bg-zinc-800 text-zinc-400"}`}>{r.payment_status?.replace(/_/g, " ")}</span>
                  <span className="text-[10px] text-zinc-600">{r.payment_method}</span>
                  <span className="text-[10px] text-zinc-600 font-mono">{r.confirmation_number}</span>
                  {r.attended && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-900/20 text-emerald-400">Attended</span>}
                </div>
                <p className="text-[#F4F1EA] text-sm truncate">{r.full_name} · {r.seats} seat{r.seats > 1 ? "s" : ""}</p>
                <p className="text-zinc-500 text-xs">{r.email} · ${((r.amount || 0) / 100).toFixed(2)} · {r.workshop_title}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                {r.payment_status === "paid" && !r.attended && (
                  <button onClick={async () => { await http.patch(`/workshops/admin/attendance/${r.workshop_id}/check-in`, { registration_id: r.id, attended: true }); toast.success("Checked in"); load(); }}
                    className="text-emerald-400 hover:text-emerald-300 p-2" title="Check in"><Users size={14} /></button>
                )}
                {r.payment_status === "pending_verification" && (
                  <button onClick={async () => { await http.patch(`/workshops/admin/registrations/${r.id}`, { payment_status: "paid" }); toast.success("Approved"); load(); }}
                    className="text-green-400 hover:text-green-300 p-2 text-[10px]" title="Approve payment"><Check size={14} /></button>
                )}
                {r.status !== "cancelled" && (
                  <button onClick={async () => { if (!window.confirm("Cancel this registration and release seats?")) return; await http.patch(`/workshops/admin/registrations/${r.id}`, { status: "cancelled" }); toast.success("Cancelled"); load(); }}
                    className="text-zinc-600 hover:text-red-400 p-2"><X size={14} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "waitlist" && (
        <div className="space-y-3">
          {waitlist.length === 0 && <p className="text-zinc-500 text-center py-8">No waitlist entries.</p>}
          {waitlist.map(w => (
            <div key={w.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[#F4F1EA] text-sm">{w.name} · {w.seats_requested} seat{w.seats_requested > 1 ? "s" : ""}</p>
                <p className="text-zinc-500 text-xs">{w.email} · {w.phone}</p>
                <p className="text-zinc-600 text-[10px]">{new Date(w.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={async () => { await http.delete(`/workshops/admin/waitlist/${w.id}`); load(); }} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "faq" && <WorkshopFAQManager />}
      {tab === "testimonials" && <WorkshopTestimonialsManager />}
      {tab === "settings" && <WorkshopSettingsManager />}
    </div>
  );
}

/* ═══ WORKSHOP EDITOR ═══ */
function WorkshopEditor({ workshop, onClose, onSaved }) {
  const [form, setForm] = useState(workshop.id ? { ...workshop } : {
    title: "", subtitle: "", slug: "", description: "", full_description: "", what_to_expect: "", who_is_for: "", what_to_bring: "",
    included: [""], date: "", day_of_week: "", start_time: "", end_time: "", duration: "",
    location_name: "", address: "", price: 0, display_price: "", capacity: 10, seats_remaining: 10,
    facilitator: "Mickey Trivett", featured_image: "", cancellation_policy: "", age_requirement: "", accessibility_info: "",
    registration_deadline: "", status: "draft", featured: false, btn_text: "Claim Your Seat", sort_order: 0,
  });
  const [saving, setSaving] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none";
  const lbl = "text-zinc-400 text-[10px] uppercase tracking-wider block mb-1";

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, included: (form.included || []).filter(Boolean) };
      if (!payload.slug) payload.slug = payload.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
      if (form.id) await http.patch(`/workshops/admin/workshops/${form.id}`, payload);
      else await http.post("/workshops/admin/workshops", payload);
      toast.success("Saved"); onSaved();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="ws-editor">
      <div className="flex justify-between items-center"><h3 className="text-[#F4F1EA] text-lg">{form.id ? "Edit" : "New"} Workshop</h3><button onClick={onClose}><X size={18} className="text-zinc-500" /></button></div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Basic Info</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={lbl}>Title *</label><input value={form.title} onChange={e => u("title", e.target.value)} className={inp} data-testid="ws-ed-title" /></div>
          <div><label className={lbl}>Subtitle</label><input value={form.subtitle || ""} onChange={e => u("subtitle", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>URL Slug</label><input value={form.slug || ""} onChange={e => u("slug", e.target.value)} className={inp} placeholder="auto-generated" /></div>
        </div>
        <div><label className={lbl}>Short Description</label><textarea value={form.description || ""} onChange={e => u("description", e.target.value)} rows={2} className={inp + " resize-none"} /></div>
        <div><label className={lbl}>Full Description</label><textarea value={form.full_description || ""} onChange={e => u("full_description", e.target.value)} rows={4} className={inp + " resize-none"} /></div>
        <div><label className={lbl}>What to Expect</label><textarea value={form.what_to_expect || ""} onChange={e => u("what_to_expect", e.target.value)} rows={3} className={inp + " resize-none"} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Who Is It For</label><input value={form.who_is_for || ""} onChange={e => u("who_is_for", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>What to Bring</label><input value={form.what_to_bring || ""} onChange={e => u("what_to_bring", e.target.value)} className={inp} /></div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Schedule & Location</p>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={lbl}>Date</label><input type="date" value={form.date || ""} onChange={e => u("date", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Day of Week</label><input value={form.day_of_week || ""} onChange={e => u("day_of_week", e.target.value)} className={inp} placeholder="Thursday" /></div>
          <div><label className={lbl}>Duration</label><input value={form.duration || ""} onChange={e => u("duration", e.target.value)} className={inp} placeholder="Two hours" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Start Time</label><input value={form.start_time || ""} onChange={e => u("start_time", e.target.value)} className={inp} placeholder="5:30 PM" /></div>
          <div><label className={lbl}>End Time</label><input value={form.end_time || ""} onChange={e => u("end_time", e.target.value)} className={inp} placeholder="7:30 PM" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Location Name</label><input value={form.location_name || ""} onChange={e => u("location_name", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Full Address</label><input value={form.address || ""} onChange={e => u("address", e.target.value)} className={inp} /></div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Pricing & Capacity</p>
        <div className="grid grid-cols-4 gap-3">
          <div><label className={lbl}>Price (cents)</label><input type="number" value={form.price || 0} onChange={e => u("price", parseInt(e.target.value) || 0)} className={inp} /></div>
          <div><label className={lbl}>Display Price</label><input value={form.display_price || ""} onChange={e => u("display_price", e.target.value)} className={inp} placeholder="$49" /></div>
          <div><label className={lbl}>Capacity</label><input type="number" value={form.capacity || 0} onChange={e => u("capacity", parseInt(e.target.value) || 0)} className={inp} /></div>
          <div><label className={lbl}>Seats Left</label><input type="number" value={form.seats_remaining ?? 0} onChange={e => u("seats_remaining", parseInt(e.target.value) || 0)} className={inp} /></div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Settings</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Status</label>
            <select value={form.status} onChange={e => u("status", e.target.value)} className={inp + " bg-[#1A1A1A]"}>
              {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Button Text</label><input value={form.btn_text || ""} onChange={e => u("btn_text", e.target.value)} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Facilitator</label><input value={form.facilitator || ""} onChange={e => u("facilitator", e.target.value)} className={inp} /></div>
          <ImageUpload value={form.featured_image || ""} onChange={v => u("featured_image", v)} folder="workshops/featured" label="Featured Image" />
        </div>
        <div><label className={lbl}>Cancellation Policy</label><textarea value={form.cancellation_policy || ""} onChange={e => u("cancellation_policy", e.target.value)} rows={2} className={inp + " resize-none"} /></div>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={form.featured || false} onChange={e => u("featured", e.target.checked)} className="accent-[#C9A055]" /> Featured workshop</label>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Included Items</p>
        {(form.included || [""]).map((item, i) => (
          <div key={i} className="flex gap-2">
            <input value={item} onChange={e => { const inc = [...(form.included || [""])]; inc[i] = e.target.value; u("included", inc); }} className={inp} placeholder="Item description" />
            {(form.included || []).length > 1 && <button onClick={() => u("included", (form.included || []).filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>}
          </div>
        ))}
        <button onClick={() => u("included", [...(form.included || []), ""])} className="text-[#C9A055] text-xs">+ Add item</button>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Past Workshop / Recap</p>
        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer"><input type="checkbox" checked={form.show_past || false} onChange={e => u("show_past", e.target.checked)} className="accent-[#C9A055]" /> Show in Past Workshops section</label>
        <div><label className={lbl}>Recap Summary</label><textarea value={form.recap || ""} onChange={e => u("recap", e.target.value)} rows={3} className={inp + " resize-none"} placeholder="What happened at this workshop..." /></div>
        <div><label className={lbl}>Key Takeaways</label>
          {(form.key_takeaways || [""]).map((t, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input value={t} onChange={e => { const kt = [...(form.key_takeaways || [""])]; kt[i] = e.target.value; u("key_takeaways", kt); }} className={inp} placeholder="Takeaway..." />
              {(form.key_takeaways || []).length > 1 && <button onClick={() => u("key_takeaways", (form.key_takeaways || []).filter((_, j) => j !== i))} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>}
            </div>
          ))}
          <button onClick={() => u("key_takeaways", [...(form.key_takeaways || []), ""])} className="text-[#C9A055] text-xs">+ Add takeaway</button>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="ws-ed-save"
          className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <button onClick={onClose} className="text-zinc-400 text-xs px-4 py-2">Cancel</button>
      </div>
    </div>
  );
}

/* ═══ WORKSHOP SETTINGS MANAGER ═══ */
function WorkshopSettingsManager() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none";
  const lbl = "text-zinc-400 text-[10px] uppercase tracking-wider block mb-1";

  useEffect(() => { http.get("/workshops/admin/settings").then(r => setForm(r.data)).catch(() => toast.error("Failed to load settings")); }, []);

  if (!form) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const uPM = (k, v) => setForm(p => ({ ...p, payment_methods: { ...p.payment_methods, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const { id, _id, ...payload } = form;
      await http.patch("/workshops/admin/settings", payload);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="ws-settings">
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Hero Section</p>
        <div><label className={lbl}>Headline</label><input value={form.hero_headline || ""} onChange={e => u("hero_headline", e.target.value)} className={inp} data-testid="ws-set-headline" /></div>
        <div><label className={lbl}>Subtext</label><textarea value={form.hero_text || ""} onChange={e => u("hero_text", e.target.value)} rows={3} className={inp + " resize-none"} data-testid="ws-set-text" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Primary Button</label><input value={form.hero_btn_primary || ""} onChange={e => u("hero_btn_primary", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Secondary Button</label><input value={form.hero_btn_secondary || ""} onChange={e => u("hero_btn_secondary", e.target.value)} className={inp} /></div>
        </div>
        <ImageUpload value={form.hero_image || ""} onChange={v => u("hero_image", v)} folder="workshops/hero" label="Hero Image" />
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Payment Methods</p>
        <div className="space-y-2">
          {[["stripe", "Stripe (Credit/Debit Card)"], ["venmo", "Venmo"], ["cashapp", "Cash App"]].map(([key, label]) => (
            <label key={key} className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.payment_methods?.[key] || false} onChange={e => uPM(key, e.target.checked)} className="accent-[#C9A055]" />
              <span className="text-sm text-zinc-300">{label}</span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Venmo Handle</label><input value={form.venmo_handle || ""} onChange={e => u("venmo_handle", e.target.value)} className={inp} /></div>
          <div><label className={lbl}>Cash App Handle</label><input value={form.cashapp_handle || ""} onChange={e => u("cashapp_handle", e.target.value)} className={inp} /></div>
        </div>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Display & SEO</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Seat Display Mode</label>
            <select value={form.seat_display || "exact"} onChange={e => u("seat_display", e.target.value)} className={inp + " bg-[#1A1A1A]"}>
              <option value="exact">Exact count</option>
              <option value="limited">Show &ldquo;Limited&rdquo; when ≤ 3</option>
              <option value="soldout_only">Only show when sold out</option>
            </select>
          </div>
          <div><label className={lbl}>Reservation Hold (minutes)</label><input type="number" value={form.reservation_minutes || 15} onChange={e => u("reservation_minutes", parseInt(e.target.value) || 15)} className={inp} /></div>
        </div>
        <div><label className={lbl}>SEO Title</label><input value={form.seo_title || ""} onChange={e => u("seo_title", e.target.value)} className={inp} /></div>
        <div><label className={lbl}>SEO Description</label><textarea value={form.seo_description || ""} onChange={e => u("seo_description", e.target.value)} rows={2} className={inp + " resize-none"} /></div>
      </div>

      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-3">
        <p className="text-[#C9A055] text-xs uppercase tracking-wider">Disclaimer</p>
        <textarea value={form.disclaimer || ""} onChange={e => u("disclaimer", e.target.value)} rows={4} className={inp + " resize-none"} />
      </div>

      <button onClick={save} disabled={saving} data-testid="ws-set-save"
        className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2.5 rounded-lg text-xs font-medium disabled:opacity-50">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
      </button>
    </div>
  );
}

/* ═══ WORKSHOP FAQ MANAGER ═══ */
function WorkshopFAQManager() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ question: "", answer: "", visible: true, sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none";
  const lbl = "text-zinc-400 text-[10px] uppercase tracking-wider block mb-1";

  const load = async () => {
    try { const { data } = await http.get("/workshops/admin/faq"); setFaqs(data); }
    catch { toast.error("Failed to load FAQs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (f) => { setEditId(f.id); setForm({ question: f.question, answer: f.answer, visible: f.visible, sort_order: f.sort_order }); };
  const startNew = () => { setEditId("new"); setForm({ question: "", answer: "", visible: true, sort_order: faqs.length }); };
  const cancel = () => { setEditId(null); setForm({ question: "", answer: "", visible: true, sort_order: 0 }); };

  const save = async () => {
    if (!form.question.trim()) { toast.error("Question is required"); return; }
    setSaving(true);
    try {
      if (editId === "new") {
        await http.post("/workshops/admin/faq", form);
      } else {
        await http.patch(`/workshops/admin/faq/${editId}`, form);
      }
      toast.success("Saved");
      cancel();
      load();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await http.delete(`/workshops/admin/faq/${id}`);
    load();
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  return (
    <div className="space-y-4" data-testid="ws-faq-admin">
      <div className="flex justify-between items-center">
        <p className="text-zinc-400 text-sm">{faqs.length} FAQ items</p>
        <button onClick={startNew} className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]" data-testid="ws-faq-add">
          <Plus size={14} /> Add FAQ
        </button>
      </div>

      {editId && (
        <div className="rounded-xl border border-[#C9A055]/30 bg-[#C9A055]/[0.03] p-5 space-y-3" data-testid="ws-faq-editor">
          <p className="text-[#C9A055] text-xs uppercase tracking-wider">{editId === "new" ? "New" : "Edit"} FAQ</p>
          <div><label className={lbl}>Question *</label><input value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} className={inp} data-testid="ws-faq-q" /></div>
          <div><label className={lbl}>Answer</label><textarea value={form.answer} onChange={e => setForm(p => ({ ...p, answer: e.target.value }))} rows={3} className={inp + " resize-none"} data-testid="ws-faq-a" /></div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
              <input type="checkbox" checked={form.visible} onChange={e => setForm(p => ({ ...p, visible: e.target.checked }))} className="accent-[#C9A055]" /> Visible
            </label>
            <div><label className={lbl}>Sort Order</label><input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className={inp + " w-20"} /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid="ws-faq-save" className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
            <button onClick={cancel} className="text-zinc-400 text-xs px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {faqs.map(f => (
          <div key={f.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start justify-between gap-4" data-testid={`ws-faq-${f.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] text-zinc-600">#{f.sort_order}</span>
                {!f.visible && <span className="text-[10px] text-red-400 bg-red-900/20 px-2 py-0.5 rounded-full">Hidden</span>}
              </div>
              <p className="text-[#F4F1EA] text-sm font-medium">{f.question}</p>
              <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{f.answer}</p>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => startEdit(f)} className="text-zinc-500 hover:text-[#C9A055] p-1"><Edit size={14} /></button>
              <button onClick={() => remove(f.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ═══ WORKSHOP TESTIMONIALS MANAGER ═══ */
function WorkshopTestimonialsManager() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", text: "", rating: 5, workshop_title: "" });
  const [saving, setSaving] = useState(false);
  const inp = "w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F4F1EA] focus:border-[#C9A055] focus:outline-none";
  const lbl = "text-zinc-400 text-[10px] uppercase tracking-wider block mb-1";

  const load = async () => {
    try { const { data } = await http.get("/workshops/admin/testimonials"); setTestimonials(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const approve = async (id, approved) => {
    await http.patch(`/workshops/admin/testimonials/${id}`, { approved });
    toast.success(approved ? "Approved" : "Unapproved");
    load();
  };
  const toggleFeat = async (id, featured) => {
    await http.patch(`/workshops/admin/testimonials/${id}`, { featured });
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await http.delete(`/workshops/admin/testimonials/${id}`);
    load();
  };
  const addTestimonial = async () => {
    if (!addForm.name.trim() || !addForm.text.trim()) { toast.error("Name and text are required"); return; }
    setSaving(true);
    try {
      await http.post("/workshops/public/testimonials", addForm);
      // Auto-approve admin-created testimonials
      const { data: all } = await http.get("/workshops/admin/testimonials");
      const newest = all[0];
      if (newest) await http.patch(`/workshops/admin/testimonials/${newest.id}`, { approved: true });
      toast.success("Testimonial added & approved");
      setShowAdd(false);
      setAddForm({ name: "", text: "", rating: 5, workshop_title: "" });
      load();
    } catch { toast.error("Failed to add"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="ws-testimonials-admin">
      <div className="flex justify-between items-center">
        <p className="text-zinc-400 text-sm">{testimonials.length} testimonials ({testimonials.filter(t => t.approved).length} approved)</p>
        <button onClick={() => setShowAdd(!showAdd)} className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37]" data-testid="ws-add-testimonial">
          <Plus size={14} /> Add Testimonial
        </button>
      </div>

      {showAdd && (
        <div className="rounded-xl border border-[#C9A055]/30 bg-[#C9A055]/[0.03] p-5 space-y-3" data-testid="ws-testimonial-form">
          <p className="text-[#C9A055] text-xs uppercase tracking-wider">Add Testimonial</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Name *</label><input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} className={inp} data-testid="ws-test-name" /></div>
            <div><label className={lbl}>Workshop Title</label><input value={addForm.workshop_title} onChange={e => setAddForm(p => ({ ...p, workshop_title: e.target.value }))} className={inp} /></div>
          </div>
          <div><label className={lbl}>Testimonial Text *</label><textarea value={addForm.text} onChange={e => setAddForm(p => ({ ...p, text: e.target.value }))} rows={3} className={inp + " resize-none"} data-testid="ws-test-text" /></div>
          <div><label className={lbl}>Rating</label>
            <div className="flex gap-1">{[1,2,3,4,5].map(n => (
              <button key={n} onClick={() => setAddForm(p => ({ ...p, rating: n }))} className={`text-lg ${n <= addForm.rating ? "text-[#D4AF37]" : "text-zinc-700"}`}>&#9733;</button>
            ))}</div>
          </div>
          <div className="flex gap-2">
            <button onClick={addTestimonial} disabled={saving} className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-50" data-testid="ws-test-submit">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save & Approve
            </button>
            <button onClick={() => setShowAdd(false)} className="text-zinc-400 text-xs px-3 py-2">Cancel</button>
          </div>
        </div>
      )}

      {testimonials.length === 0 && !showAdd && <p className="text-zinc-500 text-center py-8">No testimonials yet. Click &ldquo;Add Testimonial&rdquo; to create one manually, or they will appear here when attendees submit feedback.</p>}
      {testimonials.map(t => (
        <div key={t.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 space-y-2" data-testid={`ws-test-${t.id}`}>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.approved ? "bg-green-900/20 text-green-400" : "bg-amber-900/20 text-amber-400"}`}>{t.approved ? "Approved" : "Pending"}</span>
            {t.featured && <span className="text-[10px] text-[#C9A055]">Featured</span>}
            <span className="text-[10px] text-zinc-600">{t.workshop_title}</span>
            <span className="text-[10px] text-zinc-600">{new Date(t.created_at).toLocaleDateString()}</span>
          </div>
          <div className="flex gap-1 text-[#D4AF37] text-xs">{Array.from({ length: t.rating || 5 }).map((_, i) => <span key={i}>&#9733;</span>)}</div>
          <p className="text-zinc-300 text-sm italic">&ldquo;{t.text}&rdquo;</p>
          <p className="text-zinc-500 text-xs">— {t.name}</p>
          <div className="flex gap-2 pt-1">
            <button onClick={() => approve(t.id, !t.approved)} className={`text-[10px] px-3 py-1 rounded-full border ${t.approved ? "border-zinc-600 text-zinc-400" : "border-green-500 text-green-400"}`}>
              {t.approved ? "Unapprove" : "Approve"}
            </button>
            <button onClick={() => toggleFeat(t.id, !t.featured)} className="text-[10px] px-3 py-1 rounded-full border border-[#C9A055] text-[#C9A055]">
              {t.featured ? "Unfeature" : "Feature"}
            </button>
            <button onClick={() => remove(t.id)} className="text-[10px] px-3 py-1 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10">Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}
